import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { StatusPopupComponent } from '@control-tower/components/status-popup/status-popup.component';
import { CollaboratorsService } from '@control-tower/features/collaborators/services/collaborators.service';
import { CollaboratorContextCacheService } from '@control-tower/services/collaborator-context.service';
import { ControlTowerSmartContractService } from '@control-tower/services/control-tower-smart-contract.service';
import { EntityUpdateNotification } from '@core/models/entity-update-notification.model';
import { FirebaseService } from '@core/services/firebase/firebase.service';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Timestamp } from 'firebase/firestore';
import { from, of, Subject, Subscription, takeUntil } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import {
  ClearControlTowerContext,
  FetchCurrentCollaboratorsWithPagination,
  FetchParentControlTowerIdAndContractPartiesByControlTowerId,
  ListenForControlTowerEntityUpdates,
  SetControlTowerContext,
  SetControlTowerId,
  SetIsCollaborator,
  SetTaskFeedReadOnly,
  ShowActionStatusPopup,
  StopListeningForControlTowerEntityUpdates,
  UpdateCollaboratorFeatureMarketingMetaData,
} from './control-tower.actions';
import { ControlTowerStateModel } from './models/control-tower-state.model';
import { PermissionsTreeState } from './permissions-tree/permissions-tree.state';

const defaults: ControlTowerStateModel = {
  permissionsTree: null,
  controlTowerId: null,
  parentControlTowerId: null,
  contractId: null,
  contractType: null,
  contractParties: null,
  loggedInCollaborator: null,

  // Context-aware defaults
  userType: 'DEFAULT',
  effectiveScopes: null,
  contextLoading: false,
  entityUpdates: [],

  // Permission for features
  isTaskFeedReadOnly: false,
  currentCollaboratorsPaginated: {
    items: [],
    pageInfo: {
      hasNextPage: false,
      hasPreviousPage: false,
      pageSize: 10,
      total: 0,
    },
  },
  currentCollaboratorsOrderedByProfilePhoto: {
    items: [],
    pageInfo: {
      hasNextPage: false,
      hasPreviousPage: false,
      pageSize: 10,
      total: 0,
    },
  },
  isFeatureMarketingCTACompleted: false,
  isCollaborator: false,
};

@State<ControlTowerStateModel>({
  name: 'controlTower',
  defaults,
  children: [PermissionsTreeState],
})
@Injectable()
export class ControlTowerState {
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);
  private readonly collaboratorsService = inject(CollaboratorsService);
  private readonly collaboratorContextCache = inject(CollaboratorContextCacheService);
  private readonly firebaseService = inject(FirebaseService);
  private readonly controlTowerSmartContractService = inject(ControlTowerSmartContractService);

  private static readonly MAX_ENTITY_UPDATES = 50;
  private entityUpdatesUnSubscription = new Subject<void>();
  private activeSubscription: Subscription;

  @Selector()
  static getCurrentControlTowerId(state: ControlTowerStateModel): string | null {
    return state.controlTowerId;
  }

  /* TEMP SELECTOR */
  @Selector()
  static getIsTaskFeedReadOnly(state: ControlTowerStateModel): boolean {
    return state.isTaskFeedReadOnly;
  }

  @Selector()
  static getEffectiveScopes(state: ControlTowerStateModel): string[] {
    return state.effectiveScopes;
  }

  @Selector()
  static getCollaboratorsCount(state: ControlTowerStateModel): number {
    return state.currentCollaboratorsOrderedByProfilePhoto?.pageInfo?.total || 0;
  }

  @Selector()
  static getCurrentCollaboratorsPaginated(state: ControlTowerStateModel) {
    return state.currentCollaboratorsPaginated;
  }

  @Selector()
  static getCurrentCollaboratorsOrderedByProfilePhoto(state: ControlTowerStateModel) {
    return state.currentCollaboratorsOrderedByProfilePhoto.items;
  }

  @Selector()
  static getEntityUpdates(state: ControlTowerStateModel) {
    return state.entityUpdates;
  }

  @Selector()
  static getLatestEntityUpdates(state: ControlTowerStateModel) {
    return state.entityUpdates.length > 0 ? state.entityUpdates[state.entityUpdates.length - 1] : null;
  }

  @Selector()
  static parentControlTowerId(state: ControlTowerStateModel) {
    return state.parentControlTowerId;
  }

  @Selector()
  static contractParties(state: ControlTowerStateModel) {
    return state.contractParties;
  }

  @Selector()
  static loggedInCollaboratorId(state: ControlTowerStateModel) {
    return state?.loggedInCollaborator?._id ?? null;
  }

  @Selector()
  static loggedInCollaboratorType(state: ControlTowerStateModel) {
    return state?.userType;
  }

  @Selector()
  static contractIdAndContractParties(state: ControlTowerStateModel) {
    return {
      contractId: state?.contractId,
      contractParties: state?.contractParties,
    };
  }

  @Selector()
  static contractType(state: ControlTowerStateModel) {
    return state?.contractType;
  }

  @Selector()
  static isFeatureMarketingCTACompleted(state: ControlTowerStateModel) {
    return state?.isFeatureMarketingCTACompleted;
  }

  @Selector()
  static contractId(state: ControlTowerStateModel) {
    return state?.contractId ?? null;
  }

  @Selector()
  static controlTowerId(state: ControlTowerStateModel) {
    return state?.controlTowerId ?? null;
  }

  @Selector()
  static isCollaborator(state: ControlTowerStateModel) {
    return !!state?.isCollaborator;
  }

  @Action(SetTaskFeedReadOnly)
  setTaskFeedReadOnly(ctx: StateContext<ControlTowerStateModel>, { isTaskFeedReadOnly }: SetTaskFeedReadOnly) {
    ctx.patchState({ isTaskFeedReadOnly });
  }

  @Action(SetControlTowerId)
  setControlTowerId(ctx: StateContext<ControlTowerStateModel>, { controlTowerId }: SetControlTowerId) {
    ctx.patchState({
      controlTowerId,
    });
  }

  @Action(SetIsCollaborator)
  setIsCollaborator(ctx: StateContext<ControlTowerStateModel>, { isCollaborator }: SetIsCollaborator) {
    ctx.patchState({ isCollaborator });
  }

  @Action(SetControlTowerContext)
  setControlTowerContext(
    { patchState }: StateContext<ControlTowerStateModel>,
    { controlTowerId }: SetControlTowerContext
  ) {
    patchState({ contextLoading: true, controlTowerId });

    return from(this.collaboratorContextCache.resolveContext(controlTowerId)).pipe(
      tap(context => {
        patchState({
          userType: context.userType,
          effectiveScopes: context.effectiveScopes,
          loggedInCollaborator: context.collaborator,
          isFeatureMarketingCTACompleted: context.isFeatureMarketingCTACompleted ?? false,
          contextLoading: false,
        });
      }),
      catchError(() => {
        patchState({ contextLoading: false });
        return of(null);
      })
    );
  }

  @Action(ClearControlTowerContext)
  clearControlTowerContext({ patchState }: StateContext<ControlTowerStateModel>) {
    patchState({
      controlTowerId: null,
      userType: 'DEFAULT',
      effectiveScopes: null,
      contextLoading: false,
    });

    // Clean up entity update subscriptions when clearing context
    this.cleanupExistingSubscription();
  }
  //ATTENTION: This action listens for permission-related entity updates from the control tower.
  // Although a separate state handles entity updates, subscriptions to those updates
  // can be unsubscribed when certain components are destroyed — potentially causing
  // permission changes to be missed. This action ensures such updates are still captured.
  @Action(ListenForControlTowerEntityUpdates)
  listenForControlTowerEntityUpdates(
    ctx: StateContext<ControlTowerStateModel>,
    { controlTowerId, orgId }: ListenForControlTowerEntityUpdates
  ) {
    const { controlTowerId: currentControlTowerId } = ctx.getState();

    // Get current org id from auth state if not provided
    const {
      user: { orgId: currentOrgId },
    } = this.store.selectSnapshot(s => s.auth);

    // Clean up existing subscription if changing control tower
    if (currentControlTowerId !== controlTowerId) {
      this.cleanupExistingSubscription();
      ctx.patchState({ controlTowerId });
    }

    this.entityUpdatesUnSubscription = new Subject<void>();

    this.activeSubscription = this.firebaseService
      .getEntityUpdates(orgId ?? currentOrgId, controlTowerId)
      .pipe(
        takeUntil(this.entityUpdatesUnSubscription),
        tap(updates => {
          const entityUpdates: EntityUpdateNotification[] = updates.map(update => {
            const data = update.payload.doc.data();
            const obj = {
              entityId: data['entityId'],
              data,
              entityType: data['entityType'],
              operation: data['operation'],
              orgId: data['orgId'],
              timestamp: (data['timestamp'] as Timestamp).toMillis(),
              error: data['error'],
              message: data['message'],
            };
            return obj;
          });

          if (entityUpdates.length > 0) {
            // Filter out error updates and sort by timestamp (oldest first)
            const validUpdates = entityUpdates
              .filter(update => !update.error)
              .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));

            // Append new updates and keep only the most recent entries
            const currentUpdates = ctx.getState().entityUpdates;
            const allUpdates = [...currentUpdates, ...validUpdates];
            const trimmedUpdates =
              allUpdates.length > ControlTowerState.MAX_ENTITY_UPDATES
                ? allUpdates.slice(-ControlTowerState.MAX_ENTITY_UPDATES)
                : allUpdates;

            ctx.patchState({
              entityUpdates: trimmedUpdates,
            });
          }
        }),
        catchError(() => {
          return of(null);
        })
      )
      .subscribe();
  }

  @Action(StopListeningForControlTowerEntityUpdates)
  stopListeningForControlTowerEntityUpdates({ patchState }: StateContext<ControlTowerStateModel>) {
    this.cleanupExistingSubscription();
    patchState({ entityUpdates: [] });
  }

  @Action(FetchCurrentCollaboratorsWithPagination)
  fetchCurrentCollaboratorsWithPagination(
    { patchState }: StateContext<ControlTowerStateModel>,
    {
      controlTowerId,
      offset,
      limit,
      searchKey,
      collaboratorType,
      role,
      verified,
      sortBy,
      sortOrder,
      sortByProfilePhotosFirst,
    }: FetchCurrentCollaboratorsWithPagination
  ) {
    return this.collaboratorsService
      .getCurrentCollaboratorsWithPagination(
        controlTowerId,
        offset,
        limit,
        searchKey,
        collaboratorType,
        role,
        verified,
        sortBy,
        sortOrder,
        sortByProfilePhotosFirst
      )
      .pipe(
        tap(response => {
          if (sortByProfilePhotosFirst) {
            patchState({
              currentCollaboratorsOrderedByProfilePhoto: response.getCollaboratorsByControlTowerIdWithPagination,
            });
          } else {
            patchState({ currentCollaboratorsPaginated: response.getCollaboratorsByControlTowerIdWithPagination });
          }
        }),
        catchError(error => {
          console.error('Error fetching current collaborators with pagination:', error);
          patchState({
            currentCollaboratorsPaginated: {
              items: [],
              pageInfo: { hasNextPage: false, hasPreviousPage: false, pageSize: 10, total: 0 },
            },
          });
          return of(null);
        })
      );
  }

  @Action(ShowActionStatusPopup)
  showActionStatusPopup(_ctx: StateContext<ControlTowerStateModel>, { title, type, config }: ShowActionStatusPopup) {
    const dialogRef = this.dialog.open(StatusPopupComponent, {
      data: {
        title,
        type,
        config,
      },
      disableClose: config?.isConfirmation || false,
    });

    return dialogRef.afterClosed();
  }

  @Action(FetchParentControlTowerIdAndContractPartiesByControlTowerId)
  fetchParentControlTowerIdAndContractPartiesByControlTowerId(
    { patchState, getState }: StateContext<ControlTowerStateModel>,
    { controlTowerId }: FetchParentControlTowerIdAndContractPartiesByControlTowerId
  ) {
    return this.controlTowerSmartContractService
      .fetchParentControlTowerIdAndContractPartiesByControlTowerId(controlTowerId)
      .pipe(
        tap(response => {
          patchState({
            parentControlTowerId: response.parentControlTowerId,
            contractId: response._id,
            contractParties: response.parties,
            contractType: response?.contractType,
          });
        }),
        catchError(error => {
          console.error('Error fetching parent control tower id and contract parties by control tower id:', error);
          return of(null);
        })
      );
  }

  @Action(UpdateCollaboratorFeatureMarketingMetaData)
  updateCollaboratorFeatureMarketingMetaData(
    { patchState }: StateContext<ControlTowerStateModel>,
    { collaboratorId, featureMarketingMetaData }: UpdateCollaboratorFeatureMarketingMetaData
  ) {
    return this.collaboratorsService
      .updateCollaboratorFeatureMarketingMetaData(collaboratorId, featureMarketingMetaData)
      .pipe(
        tap(response => {
          patchState({
            loggedInCollaborator: response.updateCollaboratorFeatureMarketingMetaData,
            isFeatureMarketingCTACompleted:
              response.updateCollaboratorFeatureMarketingMetaData.featureMarketingMetaData.isCTAcompleted,
          });
        }),
        catchError(error => {
          console.error('Error updating collaborator feature marketing meta data:', error);
          return of(null);
        })
      );
  }

  // Helper method to clean up existing subscription
  private cleanupExistingSubscription(): void {
    if (this.entityUpdatesUnSubscription) {
      this.entityUpdatesUnSubscription.next();
      this.entityUpdatesUnSubscription.complete();
    }

    if (this.activeSubscription && !this.activeSubscription.closed) {
      this.activeSubscription.unsubscribe();
    }
  }
}
